import { useState, useEffect } from 'react';
import { trackButtonClick } from '../../utils/stats';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [currentPath, setCurrentPath] = useState('');

  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: '首页', path: '/index.html' },
    { name: '客户案例', path: '/cases.html' },
    { name: '关于我们', path: '/about.html' }
  ];

  const isActive = (path: string) => {
    if (path === '/index.html') {
      return currentPath === '/' || currentPath === '/index.html';
    }
    return currentPath === path;
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <a href="/index.html" className="flex items-center gap-2 z-50">
              <img 
                src="https://osscdn.fhd001.com/fhdportal/typimg/81e6b43043304376a2a1faf863540a1d.png" 
                alt="火花派智能客服" 
                className="w-8 h-8 sm:w-9 sm:h-9 object-contain"
              />
              <span className="text-base sm:text-lg font-bold text-gray-900">
                火花派智能客服
              </span>
            </a>
            
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => {
                const getFidByPath = (path: string) => {
                  if (path === '/index.html') return 'nav_home';
                  if (path === '/cases.html') return 'nav_cases';
                  if (path === '/about.html') return 'nav_about';
                  return 'nav_link';
                };
                
                return (
                  <a
                    key={link.path}
                    href={link.path}
                    onClick={(e) => {
                      e.preventDefault();
                      trackButtonClick(getFidByPath(link.path), `桌面导航栏-${link.name}`);
                      setTimeout(() => {
                        window.location.href = link.path;
                      }, 150);
                    }}
                    className={`text-base font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                      isActive(link.path)
                        ? 'text-[#0066ff]'
                        : 'text-gray-700 hover:text-[#0066ff]'
                    }`}
                  >
                    {link.name}
                  </a>
                );
              })}
            </div>
            
            <div className="hidden lg:flex items-center gap-4">
              <button
                onClick={() => {
                  trackButtonClick('free_trial_btn', '桌面导航栏免费试用按钮');
                  setShowQRCode(true);
                }}
                className="px-6 py-2.5 bg-[#0066ff] text-white text-sm font-medium rounded-full hover:bg-[#0052cc] transition-all hover:shadow-lg whitespace-nowrap cursor-pointer"
              >
                免费试用
              </button>
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 hover:text-[#0066ff] transition-colors cursor-pointer z-50"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <i className="ri-close-line text-2xl"></i>
              ) : (
                <i className="ri-menu-line text-2xl"></i>
              )}
            </button>
          </div>
        </div>
      </nav>

      <div
        className={`fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      >
        <div
          className={`fixed top-0 right-0 bottom-0 w-[280px] bg-white shadow-2xl transform transition-transform duration-300 ease-out ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col h-full pt-20 px-6 pb-6">
            <nav className="flex flex-col gap-1 mb-8">
              {navLinks.map((link) => {
                const getFidByPath = (path: string) => {
                  if (path === '/index.html') return 'nav_home';
                  if (path === '/cases.html') return 'nav_cases';
                  if (path === '/about.html') return 'nav_about';
                  return 'nav_link';
                };
                
                return (
                  <a
                    key={link.path}
                    href={link.path}
                    onClick={(e) => {
                      e.preventDefault();
                      trackButtonClick(getFidByPath(link.path), `移动端导航栏-${link.name}`);
                      setIsMobileMenuOpen(false);
                      setTimeout(() => {
                        window.location.href = link.path;
                      }, 150);
                    }}
                    className={`px-4 py-3 text-base font-medium rounded-lg transition-all cursor-pointer ${
                      isActive(link.path)
                        ? 'bg-[#0066ff]/10 text-[#0066ff]'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-[#0066ff]'
                    }`}
                  >
                    {link.name}
                  </a>
                );
              })}
            </nav>

            <button
              onClick={() => {
                trackButtonClick('free_trial_btn', '移动端导航栏免费试用按钮');
                setShowQRCode(true);
                setIsMobileMenuOpen(false);
              }}
              className="w-full px-6 py-3 bg-[#0066ff] text-white text-base font-medium rounded-full hover:bg-[#0052cc] transition-all shadow-lg whitespace-nowrap cursor-pointer"
            >
              免费试用
            </button>

            <div className="mt-auto pt-6 border-t border-gray-100">
              <p className="text-xs text-gray-400 text-center">
                火花派智能客服 © 2024
              </p>
            </div>
          </div>
        </div>
      </div>

      {showQRCode && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4"
          onClick={() => setShowQRCode(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 sm:p-8 max-w-sm w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800">扫码免费体验</h3>
              <button
                onClick={() => setShowQRCode(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <div className="bg-gray-100 p-4 sm:p-6 rounded-xl">
              <img
                src="https://static.readdy.ai/image/c9656b810d2c6cc0a8672cac16e7cf96/e7f95b852e2d7fc033ed8e2278921757.png"
                alt="QR Code"
                className="w-full h-auto"
              />
            </div>
            <p className="text-center text-xs sm:text-sm text-gray-500 mt-3 sm:mt-4">使用微信扫描二维码</p>
          </div>
        </div>
      )}
    </>
  );
}
